/** Global event (emitted by the backend `change_view_mode` command) telling
 *  every window the session flipped between live and replay. */
export const VIEW_MODE_EVENT = "view-mode-changed";

export interface ViewModePayload {
  live: boolean;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** True when this browser tab was opened as the -live interface (`?live=` flag),
 *  set by ALARM_START_TIGER's `window.open("/?live=<config>")`. Such a tab IS the
 *  -live user interface (the Live/Replay switch is shown); the webserver itself
 *  runs as the alarm interface, so this per-tab flag lives in the URL. Its
 *  *initial* mode (live or replay) follows the configured default. */
function liveFlagInUrl(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("live");
}

/** The config token carried by the -live tab URL (`?live=<config>`), or null
 *  when absent/empty. ALARM_START_TIGER opens `?live=<config_name>`; the live tab
 *  scopes its views tree to this turbine (see utils/liveScope.scopeTreeToConfig). */
export function getLiveConfig(): string | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("live");
  return v && v.length > 0 ? v : null;
}

/** True when the app was started with `-live` (so the switch should be shown). */
export async function getLiveModeEnabled(): Promise<boolean> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<boolean>("get_live_mode_enabled");
    }
    if (liveFlagInUrl()) return true;
    return await fetch("/api/get_live_mode_enabled").then((r) => r.json() as Promise<boolean>);
  } catch {
    return false;
  }
}

/** Current run state; defaults to "live" if the probe fails. */
export async function getViewRunState(): Promise<"live" | "replay"> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      const s = await invoke<string>("get_view_run_state");
      return s === "replay" ? "replay" : "live";
    }
    const s = await fetch("/api/get_view_run_state").then((r) => r.text());
    return s.includes("replay") ? "replay" : "live";
  } catch {
    return "live";
  }
}

/** Flip the session. The backend command re-broadcasts `view-mode-changed` to
 *  all windows, so callers don't emit it themselves. */
export async function setViewMode(live: boolean): Promise<void> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("change_view_mode", { live });
  } else {
    await fetch(`/api/change_view_mode?live=${live}`);
    // Browser/webserver fallback: notify same-origin windows directly.
    if (typeof BroadcastChannel !== "undefined") {
      const ch = new BroadcastChannel("tiger-view-mode");
      ch.postMessage({ live });
      ch.close();
    }
  }
}

/** File → Load Data: switch to Replay and load a turbine window (Tiger seconds). */
export async function loadReplayData(config: string, start: number, end: number): Promise<void> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("load_replay_data", { config, start, end });
  } else {
    await fetch(`/api/load_replay_data?config=${encodeURIComponent(config)}&start=${start}&end=${end}`);
  }
}

/** Advanced > data-file replay: switch to Replay and load binary data file(s). */
export async function replayDataFiles(files: string[]): Promise<void> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("replay_data_files", { files });
  } else {
    await fetch("/api/replay_data_files", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files }),
    });
  }
}

/** Whether this host reaches replay through the replay_session_manager broker. */
export async function getReplayServerSession(): Promise<boolean> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<boolean>("get_replay_server_session");
    }
    return await fetch("/api/get_replay_server_session").then((r) => r.json() as Promise<boolean>);
  } catch {
    return true; // broker assumed (Advanced hidden) on failure, matching MenuBar default
  }
}

/** Cancel an in-flight replay-window load. */
export async function replayCancelLoad(): Promise<void> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("replay_cancel_load");
  } else {
    await fetch("/api/replay_cancel_load");
  }
}
